import React, { useEffect, useState } from 'react';
import {
  getSnackBar,
  useNotification,
  showBackdropLoader,
} from 'utils/commonUtils';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { AccordianContainer } from 'components/Ui/styled';
import {
  getTrackers,
  putHideUnHideColumn,
} from 'layouts/studentTracking/services/studentTrackerApi';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { AccordionLabel } from './SsaStudentTrackersTabUtils';
import { SsaStudentProfileLoader } from '../../ssaCommonUtils';
import CustomizedSnackbars from 'components/snackbar/Snackbar';
import { TrackerColumnFormControl } from '../../ssaComponentStyled';
import { displaySnackbar } from 'components/snackbar/store/SnackbarSlice';

const SsaStudentTrackersTab = () => {
  const { NotificationPopup, handleErrorResponse } = useNotification();

  const dispatch = useDispatch();

  const ssaStudentDetail = useSelector(
    (state) => state?.ssaStudentDetail?.selectedStudentHeadingDetails || {}
  );
  const { grade } = ssaStudentDetail || {};

  const [trackers, setTrackers] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState([]);
  const [isCheckColumnLoading, setIsCheckColumnLoading] = useState(false);

  useEffect(() => {
    fetchTrackers();
  }, []);

  const fetchTrackers = async () => {
    try {
      setTabLoading(true);
      const data = await getTrackers();
      setTrackers(data);

      // Initialize all panels as expanded when data is loaded
      if (data?.trackers) {
        const allPanelIds = data?.trackers?.map((_, index) => `panel-${index}`);
        setExpandedPanels(allPanelIds);
      }

      setTabLoading(false);
    } catch (error) {
      setTabLoading(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const handleToggleColumn = async (tracker, column) => {
    const snackBar = getSnackBar('Column updated successfully');

    const payload = { columnName: column };

    try {
      setIsCheckColumnLoading(true);
      await putHideUnHideColumn(payload, tracker?.trackerId, grade);
      dispatch(displaySnackbar(snackBar));

      const updatedData = await getTrackers();
      setIsCheckColumnLoading(false);
      setTrackers(updatedData);
    } catch (error) {
      setIsCheckColumnLoading(false);
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const isColumnChecked = (tracker, col) => {
    return tracker?.hiddenColumnsByGrade?.[grade]?.includes(col) || false;
  };

  const handleChange = (panel) => (event, isExpanded) => {
    if (isExpanded) {
      setExpandedPanels((prev) => [...prev, panel]);
    } else {
      setExpandedPanels((prev) => prev.filter((p) => p !== panel));
    }
  };

  const isPanelExpanded = (panel) => {
    return expandedPanels?.includes(panel);
  };

  if (tabLoading) {
    return <SsaStudentProfileLoader />;
  }

  return (
    <>
      <CustomizedSnackbars />
      {isCheckColumnLoading && showBackdropLoader(true)}

      <Typography variant="h6" sx={{ mb: 2 }}>
        Select which tracker columns to display on the Attendance tab
      </Typography>

      <Grid container spacing={2}>
        {trackers?.trackers?.map((tracker, index) => {
          const panelName = `panel-${index}`;
          return (
            <Grid item xs={12} key={tracker.trackerId}>
              <AccordianContainer
                expanded={isPanelExpanded(panelName)}
                onChange={handleChange(panelName)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <AccordionLabel tracker={tracker} />
                </AccordionSummary>

                <AccordionDetails>
                  <Grid container spacing={3}>
                    {tracker?.columns.map((column) => (
                      <Grid item xs={12} sm={4} key={column}>
                        <TrackerColumnFormControl
                          control={
                            <Checkbox
                              checked={isColumnChecked(tracker, column)}
                              onChange={() =>
                                handleToggleColumn(tracker, column)
                              }
                            />
                          }
                          label={column}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </AccordianContainer>
            </Grid>
          );
        })}
      </Grid>
      <NotificationPopup />
    </>
  );
};

export default SsaStudentTrackersTab;
